import React from "react";

import type { WorkflowContent } from "../content/types";

type WorkflowSectionProps = {
  content: WorkflowContent;
};

export function WorkflowSection({ content }: WorkflowSectionProps) {
  const illustration = content.illustration;

  return (
    <section className="db-section section-band db-agent-section" id={content.id} aria-labelledby={`${content.id}-title`}>
      <div className="db-container db-section-container">
        <div className="section-heading db-section-heading">
          <p className="section-eyebrow db-eyebrow">{content.eyebrow}</p>
          <h2 id={`${content.id}-title`}>{content.title}</h2>
          <p className="db-section-intro">{content.lead}</p>
          {content.relatedLink ? (
            <p className="section-heading__link">
              <a href={content.relatedLink.href}>{content.relatedLink.label}</a>
            </p>
          ) : null}
        </div>

        {illustration ? (
          <div className="db-agent-illustration" role="img" aria-label={illustration.ariaLabel}>
            <div className="db-agent-input">
              <span className="db-node-kicker">{illustration.input.kicker}</span>
              <strong>{illustration.input.title}</strong>
              <p>{illustration.input.body}</p>
            </div>

            <div className="db-flow-line db-flow-line--vertical" aria-hidden="true">
              <span className="db-flow-arrow">v</span>
            </div>

            <div className="db-data-room">
              <div className="db-data-room__header">
                <div>
                  <span className="db-node-kicker">{illustration.dataRoom.kicker}</span>
                  <h3>{illustration.dataRoom.title}</h3>
                </div>
                <span className="db-status-chip">{illustration.dataRoom.status}</span>
              </div>

              <div className="db-data-room__items">
                {illustration.dataRoom.items.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>

            <div className="db-branch" aria-hidden="true">
              <span className="db-branch__line db-branch__line--left" />
              <span className="db-branch__line db-branch__line--right" />
            </div>

            <div className="db-agent-collaboration">
              <article className="db-responsibility-card db-responsibility-card--agent">
                <div className="db-card-icon" aria-hidden="true">
                  AI
                </div>
                <div className="db-card-heading">
                  <span className="db-node-kicker">{illustration.collaboration.agent.kicker}</span>
                  <h3>{illustration.collaboration.agent.title}</h3>
                </div>
                <ul>
                  {illustration.collaboration.agent.responsibilities.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <div className="db-credit-panel">
                  <div>
                    <span className="db-credit-panel__label">
                      {illustration.collaboration.agent.credits.label}
                    </span>
                    <strong>{illustration.collaboration.agent.credits.title}</strong>
                  </div>
                  <div className="db-credit-meter" aria-hidden="true">
                    <span />
                  </div>
                  <p>{illustration.collaboration.agent.credits.body}</p>
                </div>
              </article>

              <div className="db-collaboration-link" aria-hidden="true">
                <span>{illustration.collaboration.linkLabel}</span>
                <strong>&lt;-&gt;</strong>
              </div>

              <article className="db-responsibility-card db-responsibility-card--human">
                <div className="db-card-icon" aria-hidden="true">
                  OK
                </div>
                <div className="db-card-heading">
                  <span className="db-node-kicker">{illustration.collaboration.human.kicker}</span>
                  <h3>{illustration.collaboration.human.title}</h3>
                </div>
                <ul>
                  {illustration.collaboration.human.responsibilities.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <div className="db-review-panel">
                  <span className="db-review-panel__status">
                    {illustration.collaboration.human.review.status}
                  </span>
                  <p>{illustration.collaboration.human.review.body}</p>
                </div>
              </article>
            </div>

            <div className="db-flow-line db-flow-line--vertical" aria-hidden="true">
              <span className="db-flow-arrow">v</span>
            </div>

            <div className="db-confirmation-node">
              <span className="db-node-kicker">{illustration.confirmation.kicker}</span>
              <strong>{illustration.confirmation.title}</strong>
            </div>

            <div className="db-flow-line db-flow-line--vertical" aria-hidden="true">
              <span className="db-flow-arrow">v</span>
            </div>

            <div className="db-output-grid">
              {illustration.outputs.map((output, index) => (
                <div className="db-output-card" key={output.title}>
                  <span className="db-output-card__icon" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <strong>{output.title}</strong>
                  <p>{output.body}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="db-agent-steps" aria-label={illustration?.processLabel ?? content.title}>
          {content.steps.map((step, index) => (
            <article className="db-step-card" data-workflow-card data-workflow-card-family={content.id} key={step.title}>
              <span className="db-step-number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
